import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ResumeService } from 'src/services/api/ResumeService';

export const useResumeUpload = () => {
  const [isUploadReady, setIsUploadReady] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const MAX_FILE_SIZE = 2000000;
  const ACCEPTED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const validateFile = (fileList) => {
    if (!fileList || fileList.length === 0) return 'Resume is required';
    const file = fileList[0];
    if (file.size > MAX_FILE_SIZE) return 'Max file size is 5MB';
    if (!ACCEPTED_FILE_TYPES.includes(file.type))
      return 'Only .pdf, .doc, and .docx files are accepted';
    return true;
  };

  const handleFileSelection = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setFileUploadError('');
      const validationResult = validateFile([file]);
      if (validationResult === true) {
        setSelectedFile(file);
        setIsUploadReady(true);
      } else {
        setFileUploadError(validationResult);
        setIsUploadReady(false);
      }
    } catch (error) {
      setFileUploadError(error.message);
      setIsUploadReady(false);
    }
  };

  // Two-step upload (signed URL → S3 POST) as a mutation. The external
  // `uploadResume(candidateId, phone)` signature is preserved for callers; the
  // selected file travels in as a mutation variable.
  const uploadMutation = useMutation({
    mutationFn: async ({ candidateId, phone, file }) => {
      if (!file) throw new Error('No file selected');

      // Get signed URL
      const signedUrlResponse = await ResumeService.getSignedUrl({
        candidate_id: candidateId,
        phone: phone || '',
        file_name: file.name,
        content_type: file.type,
        expiration: 3600,
        file_size: file.size,
      });

      if (!signedUrlResponse?.url) {
        throw new Error('Failed to get upload URL');
      }

      // Upload to S3
      const formData = new FormData();
      Object.keys(signedUrlResponse.fields).forEach((key) => {
        formData.append(key, signedUrlResponse.fields[key]);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(signedUrlResponse.url, {
        method: 'POST',
        body: formData,
      });
      if (uploadResponse.status === 200 || uploadResponse.status === 204) {
        return {
          s3Key: `${signedUrlResponse.s3_key}`,
          uploadProcessing: Boolean(signedUrlResponse?.upload_processing),
        };
      }
      throw new Error('Failed to upload resume');
    },
    onError: (error) => setFileUploadError(error.message),
  });

  const uploadResume = (candidateId, phone) =>
    uploadMutation.mutateAsync({ candidateId, phone, file: selectedFile });

  const clearFile = () => {
    setSelectedFile(null);
    setIsUploadReady(false);
    setFileUploadError('');
  };

  return {
    selectedFile,
    isUploadReady,
    fileUploadError,
    validateFile,
    handleFileSelection,
    uploadResume,
    isUploading: uploadMutation.isPending,
    clearFile,
  };
};
